import { useState, useEffect } from "react";
import axios from "axios";
import { TrendingDown, Layers, Utensils, Download } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from "recharts";
import ApprovalFilterBanner, { KpiUnfilteredHint } from "../components/ApprovalFilterBanner";
import { useTaxYear } from "../contexts/TaxYearContext";
import TaxYearBadge from "../components/TaxYearBadge";
import ReportActions from "../components/ReportActions";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(n || 0);
const COLORS = ["#7C3AED", "#A78BFA", "#C4B5FD", "#F472B6", "#FB7185", "#FCD34D", "#6EE7B7", "#93C5FD"];

// Stage 3 — categories that belong to the CACFP program (separated to avoid double-counting perception)
const CACFP_EXPENSE_CATS = [
  "CACFP Food Expense",
  "CACFP Non-Food Expense",
  "CACFP Utilities",
  "CACFP Use Charge for Space",
];

export default function Expenses() {
  const { taxYear } = useTaxYear();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/transactions/summary?tax_year=${taxYear}`, { withCredentials: true })
      .then(res => setSummary(res.data))
      .catch(err => console.error("Summary fetch error:", err))
      .finally(() => setLoading(false));
  }, [taxYear]);

  const breakdown = summary?.expense_breakdown || {};
  const cacfpRows = CACFP_EXPENSE_CATS
    .map(cat => ({ name: cat, value: Math.round(breakdown[cat] || 0) }))
    .filter(r => r.value > 0);
  const businessRows = Object.entries(breakdown)
    .filter(([cat]) => !CACFP_EXPENSE_CATS.includes(cat))
    .map(([name, value]) => ({ name, value: Math.round(value) }))
    .filter(r => r.value > 0)
    .sort((a, b) => b.value - a.value);

  const cacfpTotal = cacfpRows.reduce((s, r) => s + r.value, 0);
  const businessTotal = businessRows.reduce((s, r) => s + r.value, 0);
  const grandTotal = cacfpTotal + businessTotal;

  if (loading) return <div className="p-8"><div className="h-64 bg-slate-100 rounded-xl animate-pulse" /></div>;

  const Section = ({ title, icon: Icon, accent, rows, subtotal, caption, testId }) => (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid={testId}>
      <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
        <div className={`w-8 h-8 ${accent.bg} rounded-lg flex items-center justify-center`}>
          <Icon className={`w-4 h-4 ${accent.text}`} />
        </div>
        <div className="flex-1">
          <h2 className="font-bold text-slate-900 text-base" style={{ fontFamily: "Manrope" }}>{title}</h2>
          {caption && <p className="text-xs text-slate-400 mt-0.5">{caption}</p>}
        </div>
        <div className="text-right">
          <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Sub-total</p>
          <p className={`text-xl font-bold ${accent.text}`} style={{ fontFamily: "Manrope" }} data-testid={`${testId}-subtotal`}>{fmt(subtotal)}</p>
        </div>
      </div>
      {rows.length > 0 ? (
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-100">
              <th className="text-left px-6 py-2.5 text-xs font-semibold text-slate-500 uppercase">Category</th>
              <th className="text-right px-6 py-2.5 text-xs font-semibold text-slate-500 uppercase">Amount</th>
              <th className="text-right px-6 py-2.5 text-xs font-semibold text-slate-500 uppercase">% of section</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((item, i) => (
              <tr key={item.name} className="border-b border-slate-50 hover:bg-slate-50">
                <td className="px-6 py-2.5">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                    <span className="text-slate-700">{item.name}</span>
                  </div>
                </td>
                <td className="px-6 py-2.5 text-right text-red-600 font-semibold">{fmt(item.value)}</td>
                <td className="px-6 py-2.5 text-right text-slate-400 text-xs">{subtotal > 0 ? ((item.value / subtotal) * 100).toFixed(1) : 0}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-center py-8 text-slate-400 text-sm">No items in this section yet.</p>
      )}
    </div>
  );

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
            <TrendingDown className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>Expenses</h1>
              <TaxYearBadge />
            </div>
            <p className="text-slate-500 text-sm mt-1">Pure business + CACFP-allocated, separated for clarity.</p>
          </div>
        </div>
        <ReportActions
          taxYear={taxYear}
          category="Expenses"
          source="/api/reports/expenses-pdf"
          sourceParams={{}}
          filenameHint={`expenses-${taxYear}.pdf`}
          testId="expenses"
        />
      </div>

      <ApprovalFilterBanner summary={summary} />

      {/* Pure Business Expenses */}
      <Section
        title="Pure Business Expenses"
        icon={Layers}
        accent={{ bg: "bg-purple-100", text: "text-purple-700" }}
        rows={businessRows}
        subtotal={businessTotal}
        caption="Operating costs not tied to the CACFP food program."
        testId="pure-business-expenses-card"
      />

      {/* CACFP-Allocated Expenses */}
      <Section
        title="CACFP-Allocated Expenses"
        icon={Utensils}
        accent={{ bg: "bg-blue-100", text: "text-blue-700" }}
        rows={cacfpRows}
        subtotal={cacfpTotal}
        caption="Reimbursed via CACFP Income. See CACFP Overview for full detail."
        testId="cacfp-allocated-expenses-card"
      />

      {/* Grand Total */}
      <div className="bg-slate-900 text-white rounded-xl shadow-sm p-6 flex items-center justify-between" data-testid="expenses-grand-total-card">
        <div>
          <p className="text-xs text-slate-400 uppercase font-semibold tracking-wider">Total Expenses (Schedule C Line 28)</p>
          <p className="text-4xl font-bold mt-1" style={{ fontFamily: "Manrope" }} data-testid="expenses-grand-total-value">{fmt(grandTotal)}</p>
          <KpiUnfilteredHint unfiltered={summary?.unfiltered_total_expenses} approved={summary?.total_expenses} testId="expenses-total-unfiltered-hint" />
        </div>
        <div className="text-right text-xs text-slate-400 max-w-xs">
          CACFP-allocated expenses are <strong className="text-white">included</strong> in your total — they're deductible on Schedule C alongside your pure business expenses.
        </div>
      </div>

      {/* Distribution Chart */}
      {grandTotal > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="expense-distribution-chart">
          <h2 className="font-bold text-slate-900 text-base mb-4" style={{ fontFamily: "Manrope" }}>Pure Business vs. CACFP-Allocated</h2>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={[
                  { name: "Pure Business", value: businessTotal },
                  { name: "CACFP-Allocated", value: cacfpTotal },
                ].filter(d => d.value > 0)}
                cx="50%" cy="50%" innerRadius={70} outerRadius={110} dataKey="value" paddingAngle={2}
              >
                <Cell fill="#7C3AED" />
                <Cell fill="#2563EB" />
              </Pie>
              <Tooltip formatter={v => fmt(v)} />
              <Legend iconType="circle" iconSize={8} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}

      {grandTotal === 0 && (
        <div className="text-center py-16 text-slate-400">
          <p className="text-lg font-semibold mb-2" style={{ fontFamily: "Manrope" }}>No expense data yet</p>
          <p className="text-sm">Upload bank statements and receipts to see expense breakdown.</p>
        </div>
      )}
    </div>
  );
}
